import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { toast } from "sonner";
import { Loader2, ShieldAlert, CheckCircle2, ArrowRight } from "lucide-react";

const DEC = {
  proceed: { c: "text-donkey-green", t: "Proceed" },
  pivot: { c: "text-donkey-amber", t: "Pivot" },
  more_evidence: { c: "text-donkey-amber", t: "More evidence required" },
  stop: { c: "text-donkey-red", t: "Stop" },
};

export default function BuildStart() {
  const { reportId, oppIndex } = useParams();
  const navigate = useNavigate();
  const [gate, setGate] = useState(null);
  const [err, setErr] = useState(null);
  const [ack, setAck] = useState(false);
  const [override, setOverride] = useState("");
  const [revised, setRevised] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/build/eligibility/${reportId}/${oppIndex}`).then(({ data }) => setGate(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Not eligible."));
  }, [reportId, oppIndex]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/build/businesses", {
        report_id: reportId, opportunity_index: Number(oppIndex),
        acknowledged: ack, override_reason: override, revised_opportunity: revised,
      });
      if (gate.existing_business_id) toast.success("Resuming your build.");
      navigate(`/build/${data.business_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start build.");
      setBusy(false);
    }
  };

  if (err) return <div className="mx-auto max-w-2xl px-6 py-20 text-center"><ShieldAlert className="w-10 h-10 text-donkey-amber mx-auto mb-4" /><p className="text-donkey-warm">{err}</p><Button className="mt-6 bg-donkey-orange text-donkey-black" onClick={() => navigate("/pricing")}>See plans</Button></div>;
  if (!gate) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-donkey-orange" /></div>;

  const o = gate.opportunity;
  const dec = gate.latest_decision;
  const canProceed = gate.can_build && (
    dec === "proceed" || (["more_evidence", null, undefined].includes(dec) && ack) ||
    (dec === "pivot" && revised.trim()) || (dec === "stop" && override.trim())
  );

  return (
    <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
      <div className="flex items-center gap-3 mb-6"><DonkeyMascot size={56} /><div><p className="text-xs uppercase tracking-[0.2em] text-donkey-orange">Stage 3 · Build</p><h1 className="font-heading font-extrabold text-3xl text-donkey-warm">Turn this into a business</h1></div></div>

      <div className="card-donkey rounded-2xl p-6 space-y-4" data-testid="build-gate">
        <div><p className="text-xs uppercase tracking-wider text-donkey-grey">Opportunity</p><p className="text-donkey-warm font-heading font-semibold text-lg">{o.name}</p><p className="text-sm text-donkey-grey">{o.proposition}</p></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><p className="text-xs uppercase tracking-wider text-donkey-grey">Original verdict</p><p className="text-donkey-warm">{o.verdict_label}</p><p className="text-xs text-donkey-grey mt-1">{o.verdict_explanation}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-donkey-grey">Latest Stage 2 decision</p><p className={`font-semibold ${(DEC[dec] || {}).c || "text-donkey-grey"}`} data-testid="gate-decision">{(DEC[dec] || {}).t || "No decision yet"}</p>{gate.decision_detail?.rationale && <p className="text-xs text-donkey-grey mt-1">{gate.decision_detail.rationale}</p>}</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><p className="text-xs uppercase tracking-wider text-donkey-grey">Validation evidence</p><p className="text-sm text-donkey-warm">{gate.has_validation_evidence ? "Recorded" : "None recorded"} · quality: {o.latest_validation_evidence_quality || "—"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-donkey-grey">Minimum Loss Limit</p><p className="text-sm text-donkey-warm">{typeof o.minimum_loss_limit === "object" ? `£${o.minimum_loss_limit?.max_money_gbp || "?"} · ${o.minimum_loss_limit?.max_hours || "?"}h` : (o.minimum_loss_limit || "—")}</p></div>
        </div>
        {o.assumptions?.length > 0 && <div><p className="text-xs uppercase tracking-wider text-donkey-grey mb-1">Important assumptions</p><ul className="text-sm text-donkey-grey list-disc pl-5">{o.assumptions.slice(0, 4).map((a, i) => <li key={i}>{typeof a === "string" ? a : a?.text || JSON.stringify(a)}</li>)}</ul></div>}
      </div>

      {/* Decision-specific gate */}
      <div className="mt-6">
        {dec === "proceed" && <p className="text-sm text-donkey-green flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> This opportunity has a Proceed decision. You're clear to build.</p>}
        {["more_evidence", null, undefined].includes(dec) && (
          <label className="flex items-start gap-3 text-sm text-donkey-warm/90 card-donkey rounded-xl p-4" data-testid="gate-ack">
            <Checkbox checked={ack} onCheckedChange={setAck} className="mt-0.5" />
            <span>This opportunity does not have a Proceed decision. I acknowledge I am building on incomplete evidence, at my own risk.</span>
          </label>
        )}
        {dec === "pivot" && <div><p className="text-sm text-donkey-amber mb-2">This was a Pivot. Confirm the revised opportunity/offer you intend to build:</p><Textarea value={revised} onChange={(e) => setRevised(e.target.value)} className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="gate-pivot" /></div>}
        {dec === "stop" && <div><p className="text-sm text-donkey-red mb-2">This opportunity was Stopped. To override, record your reason (this is stored):</p><Textarea value={override} onChange={(e) => setOverride(e.target.value)} className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="gate-override" /></div>}
      </div>

      <Button onClick={start} disabled={!canProceed || busy} className="mt-8 bg-donkey-orange text-donkey-black font-semibold h-12 px-7" data-testid="start-build-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open the Build workspace <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </div>
  );
}
